import { RootState } from "store";
import styled from "styled-components";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Radio, RadioChangeEvent, Row, Select, Tooltip, Typography } from "antd";
import {
  addNode,
  clearDropArea,
  addActionModal,
  editNode,
  updateErrorNodes,
  setSelectedIfElsesAction,
  getEmailsLinks,
} from "../actions";
import SelectWithIcon from "modules/Campaigns/Shared/SelectWithIcon";
import { findDropAreaData } from "../utils";
import { ControlNameEnum, ConditionEmails, ConditionEnum, IControls } from "../interfaces";

type IConditionControls = {
  id: string | number;
  value: ConditionEnum.HAS_CLICKED | ConditionEnum.HAS_OPENED;
  label: ConditionEnum.HAS_CLICKED | ConditionEnum.HAS_OPENED;
};

const { Option, OptGroup } = Select;
const { Paragraph } = Typography;

interface IConditionSelection {
  id: string;
  emails: ConditionEmails[];
  nodes: any;
  conditions?: any;
  condition?: any;
  // conditionControls: IConditionControls[];
  conditionOperators?: any;
  setConditions: (id: any) => void;
  setConditionOperators: (val: any) => void;
  enableSaveButton: (value: boolean) => void;
  index?: any;
  isAdditional?: boolean;
  removeCondition?: any;
  updateNode: any;
}

const ConditionSelection: React.FC<IConditionSelection> = (props) => {
  const {
    id,
    index,
    emails,
    nodes,
    conditionOperators,
    condition,
    conditions,
    setConditions,
    setConditionOperators,
    enableSaveButton,
    // conditionControls,
    removeCondition,
    updateNode,
    isAdditional,
  } = props;
  // Redux States
  const controls: IControls[] = useSelector((state: RootState) => state.automation.controls);
  const emailsLinks: any = useSelector((state: RootState) => state.automation.emailsLinks.data);
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[id]?.detail);
  const workflowId = useSelector((state: RootState) => state.automation.workflow.detail.id);
  const modal = useSelector((state: RootState) => state.automation.modals.addAction);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  // Local States
  let conditionControls: IConditionControls[] = controls
    .find((control: IControls) => control.name === ControlNameEnum.IF_ELSE)
    ?.conditions?.map((condition) => {
      return {
        id: condition.id,
        value: condition.name,
        label: condition.name,
      };
    }) || [{ id: "", value: "", label: "" }];

  const [selectedCondition, setSelectedCondition] = useState<IConditionControls>(conditionControls[0]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedEmailLinks, setSelectedEmailLinks] = useState<any>([]);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedLinkType, setSelectedLinkType] = useState(null);

  // Local variables
  const operatorOptions = [
    { label: "AND", value: "AND" },
    { label: "OR", value: "OR" },
  ];

  // Component Logic
  const onChangeOperator = ({ target: { value } }: RadioChangeEvent) => {
    let operators = conditionOperators?.map((operator: any) => (operator = value));
    setConditionOperators(operators);
  };

  const onEmailChange = (value: any) => {
    let conditionObject = { ...conditions[index] };
    if (
      value === undefined ||
      (selectedEmail !== value && selectedCondition.value === ConditionEnum.HAS_CLICKED)
    ) {
      setSelectedLink(null);
      setSelectedLinkType(null);
      setSelectedEmail(value);
      conditionObject.linkType = null;
      conditionObject.link = null;
    } else {
      setSelectedEmail(value);
    }
    if (value) {
      let email = emails.find((email) => value == email?.sp_uuid || email.emailId == parseInt(value));
      let condition = {
        ...conditionObject,
        emailId: email?.emailId,
        emailName: email?.title,
        emailType: email?.sp_uuid ? "share profile" : "email", // 10 is for shared email
        ...(email?.sp_uuid ? { sp_uuid: email?.sp_uuid } : { sp_uuid: "" }),
      };
      updateNode(condition, index);
      let links = emailsLinks[value];
      setSelectedEmailLinks(links);
    }
  };

  const onLinkChange = (value: any) => {
    let values = value?.split("#-#");
    let condition = {
      ...conditions[index],
    };
    // #-#
    if (value !== undefined) {
      setSelectedLink(values[1]);
      setSelectedLinkType(values[0]);
      condition = { ...condition, link: values[1], linkType: values[0] };
    } else {
      setSelectedLink(null);
      setSelectedLinkType(null);
      condition = { ...condition, link: null, linkType: null };
    }
    updateNode(condition, index);
  };

  const selectHandler = (value: any) => {
    setSelectedCondition(value);
    let condition = {
      ...conditions[index],
      controlName: value.value,
      controlId: value.id,
    };
    if (value.id === 4 && !selectedLink) {
      if (selectedEmail) {
        let links = emailsLinks[selectedEmail];
        setSelectedEmailLinks(links);
      }
    } else if (value.id === 3 && selectedEmail) {
      setSelectedLink(null);
      setSelectedLinkType(null);
    }
    if (!selectedEmail && condition.emailId) {
      condition.emailId = "";
      condition.emailName = "";
      condition.emailType = "";
      condition.sp_uuid = "";
    }
    updateNode(condition, index);
  };

  const clearEmailHandler = () => {
    setSelectedEmail("");
    setSelectedLink(null);
    setSelectedLinkType(null);
    enableSaveButton(false);
    // Setting the condition to initial state
    let condition = {
      emailId: 0,
      emailType: "",
      emailName: "",
      link: "",
      linkType: "",
      sp_uuid: "",
      controlId: selectedCondition.id,
      controlName: selectedCondition.value,
    };
    updateNode(condition, index);
  };

  // useEffect to set the data of edit Node
  const isConditionModalOpen = modal.visible === true && modal.tab === ControlNameEnum.IF_ELSE;
  let dependencies = itemData && isConditionModalOpen && emailsLinks;
  useEffect(() => {
    if (isConditionModalOpen && itemData && Object.keys(emailsLinks).length > 0) {
      let conditionObject = { ...condition };
      if (Object.keys(conditionObject).length > 0) {
        let emailId = conditionObject?.emailId;
        let indexOfEmail = emails.findIndex((email) => email?.emailId?.toString() === emailId?.toString());
        setSelectedEmailLinks(emailsLinks[emailId] || []);
        setSelectedCondition({
          id: conditionObject.controlId,
          value: conditionObject.controlName,
          label: conditionObject.controlName,
        });
        if (indexOfEmail > -1) {
          setSelectedEmail(emailId ?? conditionObject?.sp_uuid);
        }
        if (
          emailId && indexOfEmail > -1
            ? conditionObject.link && emailsLinks[emailId]
            : conditionObject.link === "Any Link"
        ) {
          if (conditionObject?.linkType) {
            let indexOf = emailsLinks[emailId]?.findIndex((item: any) =>
              item.type === "buttons"
                ? conditionObject?.linkType === "buttons" || conditionObject?.linkType === "articles"
                : item.type === conditionObject?.linkType
            );
            if (indexOf > -1) {
              let linkIndex = emailsLinks[emailId][indexOf]?.options.findIndex(
                (item: any) => item.value === conditionObject.link
              );
              if (linkIndex > -1) {
                setSelectedLink(conditionObject.link);
                setSelectedLinkType(conditionObject?.linkType);
              } else {
                setSelectedLink(null);
                setSelectedLinkType(null);
                enableSaveButton(false);
              }
            } else if (conditionObject.link === "Any Link") {
              setSelectedLink(conditionObject.link);
              setSelectedLinkType(conditionObject?.linkType);
            }
          } else {
            setSelectedLink(conditionObject.link);
            setSelectedLinkType(conditionObject?.linkType);
          }
        } else {
          enableSaveButton(false);
          setSelectedLink(null);
          setSelectedLinkType(null);
        }
      }
    } else if (isConditionModalOpen && selectedEmail !== "") {
      setSelectedEmail("");
      setSelectedCondition(conditionControls[0]);
      enableSaveButton(false);
    }
  }, [dependencies, conditions.length]);

  useEffect(() => {
    if (modal.visible === false && selectedEmail !== "") {
      setSelectedEmail("");
      setSelectedLink(null);
      setSelectedLinkType(null);
      setSelectedCondition(conditionControls[0]);
    }
  }, [modal]);

  useEffect(() => {
    if (selectedCondition && selectedEmail) {
      setSelectedIfElsesAction(parseInt(selectedCondition.id.toString()), parseInt(selectedEmail));
    } else {
      setSelectedIfElsesAction(0, 0);
    }
  }, [selectedCondition, selectedEmail]);

  useEffect(() => {
    if (!actionModalVisible) {
      setSelectedIfElsesAction(0, 0);
      if (selectedEmail && selectedEmail?.length > 0 && selectedEmail?.includes("-")) {
        setSelectedEmail("");
      }
    } else {
      if (emails.length > 0) {
        let emailIds: string = emails
          .filter((email) => email.emailId)
          .map((email) => email.emailId)
          .join(",");
        let nodeIds: string = nodes
          .filter((node: any) => node.nodeId)
          .map((node: any) => node.nodeId)
          .join(",");
        getEmailsLinks(emailIds, nodeIds, workflowId);
        // call the api for links
      }
    }
  }, [actionModalVisible]);

  return (
    <Wrapper>
      {isAdditional ? (
        <div>
          <Radio.Group
            options={operatorOptions}
            value={conditionOperators ? conditionOperators[0] : "AND"}
            optionType="button"
            buttonStyle="solid"
            onChange={onChangeOperator}
          />
        </div>
      ) : null}
      <div className="headingStyles">
        <span className="label-condition">Select a condition</span>
        {isAdditional ? (
          <span className="removeBtn" onClick={() => removeCondition(index)}>
            Remove
          </span>
        ) : null}
      </div>

      <Row gutter={[32, 0]}>
        <Col span={12}>
          <SelectWithIcon
            defaultValue={{ value: "Select a condition", label: "Select a condition" }}
            options={conditionControls}
            onChange={selectHandler}
            value={selectedCondition}
            className="if-else-dropdown"
            menuWidth={267}
          />
        </Col>
        <Col span={12}>
          <Select
            className="condition-dropdown w-100"
            showSearch
            placeholder="Please select email"
            optionFilterProp="children"
            onChange={onEmailChange}
            allowClear
            value={
              emails.find(
                (email) =>
                  (email?.sp_uuid && email?.sp_uuid?.toString() === selectedEmail?.toString()) ||
                  (email?.emailId && email?.emailId?.toString() == selectedEmail?.toString())
              )?.title
            }
            // onClear={handleOnClear}
            filterOption={(input, option) =>
              (option!.children as unknown as string).toLowerCase().includes(input.toLowerCase())
            }
            onClear={clearEmailHandler}
            defaultActiveFirstOption={false}
            // open={true}
            // popupClassName="conditions-list"
          >
            {emails?.map((email: ConditionEmails, index) => {
              let eId = `${!email.emailId ? email.sp_uuid : email.emailId}`;
              if (email.emailId || email.sp_uuid) {
                return (
                  <Option className="automation-dropdown-option" key={index} value={eId}>
                    {email.title}
                  </Option>
                );
              }
            })}
          </Select>
        </Col>
        {selectedCondition.value === ConditionEnum.HAS_CLICKED && selectedEmail && (
          <Col span={12}>
            <Select
              className="condition-dropdown link-condition w-100"
              showSearch={false}
              placeholder="Please select links"
              // optionFilterProp="children"
              onChange={onLinkChange}
              allowClear
              // open={true}
              // value={selectedEmailsLinks?.find((linkObj) => linkObj.value === selectedLink)}
              value={selectedLinkType && selectedLink ? `${selectedLinkType}#-#${selectedLink}` : null}
              filterOption={(input, option) =>
                (option!.value as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              onClear={() => {
                setSelectedLink(null);
                setSelectedLinkType(null);
                enableSaveButton(false);
              }}
              getPopupContainer={() => document.querySelectorAll(".condition-wrapper")[0] as HTMLElement}
            >
              {[{ value: "Any Link", label: "Any Link" }, ...(selectedEmailLinks ? selectedEmailLinks : [])].map(
                (item: any) => {
                  let obj = item.options ? (
                    item.options.length > 0 ? (
                      <OptGroup label={item.label}>
                        {item?.options?.map((option: any) => {
                          let popoverText =
                            option.label == option.value ? (
                              option.label
                            ) : (
                              <>
                                <div>{option.label}</div>
                                <div>{option.value}</div>
                              </>
                            );
                          let value = `${option.type}#-#${option.value}`;
                          let readMoreText =
                            option.type === "articles"
                              ? "Read More"
                              : option.type === "buttons"
                              ? option.label
                              : "";
                          let labelText =
                            option.type === "articles"
                              ? `: Article (${option.label})`
                              : option.type === "buttons"
                              ? ""
                              : option.label;
                          return (
                            <Option className="text-underlined" value={value}>
                              <Tooltip placement="bottom" title={popoverText}>
                                <p className="read-more">{readMoreText}</p>
                                {labelText.length > 0 ? (
                                  <Paragraph className="camp-name" ellipsis={true}>
                                    {labelText}
                                  </Paragraph>
                                ) : null}
                              </Tooltip>
                            </Option>
                          );
                        })}
                      </OptGroup>
                    ) : null
                  ) : (
                    <Option
                      className={item.label === "Any Link" ? "any-link" : "text-underlined"}
                      value={`Any Link#-#${item.value}`}
                    >
                      {item.label}
                    </Option>
                  );
                  return obj;
                }
              )}
            </Select>
          </Col>
        )}
      </Row>
    </Wrapper>
  );
};

export default ConditionSelection;

const Wrapper = styled.div`
  .headingStyles {
    margin-top: 15px;
    margin-bottom: 5px;
  }
`;
